import { configureStore } from '@reduxjs/toolkit';
import { shallowEqual, type TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import amplitudeAnalyticsSettings from '@alltrails/amplitude/slices/amplitudeAnalyticsSettings';
import context from '@alltrails/context/slices/contextSlice';
import currencyCode from '@alltrails/context/slices/currencyCodeSlice';
import plans from '@alltrails/context/slices/plansSlice';
import type RequiredPreloadedState from '@alltrails/core/types/RequiredPreloadedState';
import experiments from '@alltrails/experiments/slices/experiments';
import features from '@alltrails/features/slices/features';
import type Context from '@alltrails/types/Context';
import type CountryCode from '@alltrails/types/CountryCode';
import upsellContent from '@alltrails/upsell/slices/upsellContent';

export type PreloadedState = RequiredPreloadedState;

export const reducer = {
  amplitudeAnalyticsSettings,
  browser: (state = {}) => state,
  context,
  currencyCode,
  experiments,
  features,
  irclickid: (state = '') => state,
  plans,
  requestCountryCode: (state = '' as CountryCode) => state,
  upsellContent
};

export const buildStore = (preloadedState?: PreloadedState) =>
  configureStore({ reducer, preloadedState, middleware: getDefaultMiddleware => getDefaultMiddleware({ immutableCheck: false }) });

export type AppStore = ReturnType<typeof buildStore>;
export type AppDispatch = AppStore['dispatch'];
export type RootState = ReturnType<AppStore['getState']> & { context?: Context };

// these are intended to use within the app only
export const useAppDispatch = (): AppDispatch => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = (selector: (state: RootState) => any) =>
  useSelector<RootState, any>(selector, shallowEqual);
